/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, July 2020
 */

import {
  Action,
  Condition,
  ConditionalStatement,
  FlowBlockRoot,
  FlowBlockInput,
  FlowBlockQuestion,
} from '@staffino/model';
import { FormContextValues } from 'react-hook-form/dist/contextTypes';
import { parseISO } from 'date-fns';
import FlowStateManager from '../flow-state-manager';
import { IFlowBridge, QuestionObject } from '../flow-bridge';
import { ConditionEvaluatorContext } from './condition-evaluator-context';
import answerEvaluator from './conditions/answer';
import inputEvaluator from './conditions/input';
import legacyAnswer from './conditions/legacy-answer';
import legacyParameter from './conditions/legacy-parameter';
import {
  customerAttribute,
  interactionAttribute,
} from './conditions/attribute';
import { interactionSource } from './conditions/interaction-source';
import { interactionReason } from './conditions/interaction-reason';
import getQuestionType from './conditions/get-question-type';
import target from './conditions/target';
import parameter from './conditions/parameter';
import structure from './conditions/structure';
import openUrl from './open-url';

type EvaluatorFunction = (
  condition: Condition,
  context: ConditionEvaluatorContext,
) => Promise<boolean>;

export default class ConditionEvaluator {
  evaluatorFunctionMap: Map<string, EvaluatorFunction> = new Map();

  constructor(
    private readonly stateManager: FlowStateManager,
    private readonly bridge: IFlowBridge,
    private readonly definition: FlowBlockRoot,
    private readonly form: FormContextValues,
  ) {
    this.evaluatorFunctionMap.set('Flow.Answer', legacyAnswer);
    this.evaluatorFunctionMap.set('Parameter', legacyParameter);
    this.evaluatorFunctionMap.set('answer', answerEvaluator);
    this.evaluatorFunctionMap.set('input', inputEvaluator);
    this.evaluatorFunctionMap.set('legal', inputEvaluator);
    this.evaluatorFunctionMap.set('interaction.source', interactionSource);
    this.evaluatorFunctionMap.set('interaction.reason', interactionReason);
    this.evaluatorFunctionMap.set('customer.attribute', customerAttribute);
    this.evaluatorFunctionMap.set(
      'interaction.attribute',
      interactionAttribute,
    );
    this.evaluatorFunctionMap.set('target', target);
    this.evaluatorFunctionMap.set('parameter', parameter);
    this.evaluatorFunctionMap.set('structure', structure);
    this.evaluatorFunctionMap.set('legacyStructure', structure);
  }

  private async evaluateStatement(statement: ConditionalStatement) {
    const ifResult =
      statement.if && (await this.evaluateCondition(statement.if));
    const unlessResult =
      statement.unless && (await this.evaluateCondition(statement.unless));
    if (ifResult && !unlessResult) {
      this.applyActions(statement.actions);
    }
  }

  private async evaluateCondition(condition: Condition): Promise<boolean> {
    const context = {
      stateManager: this.stateManager,
      bridge: this.bridge,
      definition: this.definition,
    };
    const evaluator = this.evaluatorFunctionMap.get(condition[0]);
    if (evaluator) {
      return evaluator(condition, context);
    }
    if (condition?.[0] === '||') {
      for (let i = 1; i < condition.length; i += 1) {
        if (await this.evaluateCondition(condition[i])) {
          return true;
        }
      }
      return false;
    }
    if (condition?.[0] === '&&') {
      for (let i = 1; i < condition.length; i += 1) {
        if (!(await this.evaluateCondition(condition[i]))) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  private applyAction(action: Action) {
    if (action[0] === 'link') {
      const url = action[1];
      openUrl(url);
    }
    if (action[0] === 'Flow.SetNextPage') {
      this.stateManager.setNextPageId(action[1]);
    }
    if (action[0] === 'Flow.Advance') {
      this.stateManager.setNextPageId(action[1] || undefined);
      this.stateManager.advancePage();
    }
    if (action[0] === 'Flow.Restart' || action[0] === 'flow.restart') {
      // for restarting from navigation bar button
      this.stateManager.restartFlow();
    }
    if (action[0] === 'Flow.Finish' || action[0] === 'flow.close') {
      // for restarting from navigation bar button
      this.stateManager.finishFlow();
    }
    if (action[0] === 'Block.Show') {
      console.debug('[EVAL] Block.Show', action[1]);
      this.stateManager.setOverride(action[1], { hidden: false });
    }
    if (action[0] === 'Block.Hide') {
      console.debug('[EVAL] Block.Hide', action[1]);
      this.stateManager.setOverride(action[1], { hidden: true });
    }

    if (action[0] === 'block.show') {
      console.debug('[EVAL] block.show', action[1]);

      // if block ID contains '/comment' suffix, then this is action on question's follow-up
      if (action[1].blockId.endsWith('/comment')) {
        this.stateManager.setOverride(
          // remove '/comment' suffix to get component ID
          action[1].blockId.substr(0, action[1].blockId.indexOf('/')),
          { commentAllowed: true },
        );
      } else {
        this.stateManager.setOverride(action[1].blockId, { hidden: false });
      }
    }

    if (action[0] === 'block.hide') {
      console.debug('[EVAL] block.hide', action[1]);

      // if block ID contains '/comment' suffix, then this is action on question's follow-up
      if (action[1].blockId.endsWith('/comment')) {
        this.stateManager.setOverride(
          // remove '/comment' suffix to get component ID
          action[1].blockId.substr(0, action[1].blockId.indexOf('/')),
          { commentAllowed: false },
        );
      } else {
        this.stateManager.setOverride(action[1].blockId, { hidden: true });
      }
    }

    if (action[0] === 'page.show') {
      console.debug('[EVAL] page.show', action[1]);
      this.stateManager.setOverride(action[1].blockId, { hidden: false });
    }
    if (action[0] === 'page.hide') {
      console.debug('[EVAL] page.hide', action[1]);
      this.stateManager.setOverride(action[1].blockId, { hidden: true });
    }

    if (action[0] === 'makeRequired') {
      console.debug('[EVAL] makeRequired', action[1]);

      // if block ID contains '/comment' suffix, then this is action on question's follow-up
      if (action[1].blockId.endsWith('/comment')) {
        this.stateManager.setOverride(
          // remove '/comment' suffix to get component ID
          action[1].blockId.substr(0, action[1].blockId.indexOf('/')),
          { commentRequired: true },
        );
      } else {
        this.stateManager.setOverride(action[1].blockId, { required: true });
      }
    }

    if (action[0] === 'makeOptional') {
      console.debug('[EVAL] makeOptional', action[1]);

      // if block ID contains '/comment' suffix, then this is action on question's follow-up
      if (action[1].blockId.endsWith('/comment')) {
        this.stateManager.setOverride(
          // remove '/comment' suffix to get component ID
          action[1].blockId.substr(0, action[1].blockId.indexOf('/')),
          { commentRequired: false },
        );
      } else {
        this.stateManager.setOverride(action[1].blockId, { required: false });
      }
    }

    if (action[0] === 'addLabel') {
      console.debug('[EVAL] addLabel', action[1]);
      const { label } = action[1];
      this.stateManager.setLabelOverride(label);
    }

    if (action[0] === 'customer.setAttribute') {
      console.debug('[EVAL] customer.setAttribute', action[1]);
      const { attribute, value } = action[1];
      this.stateManager.setAttributeOverride(`customer.${attribute}`, value);
    }
    if (action[0] === 'interaction.setAttribute') {
      console.debug('[EVAL] interaction.setAttribute', action[1]);
      const { attribute, value } = action[1];
      this.stateManager.setAttributeOverride(`interaction.${attribute}`, value);
    }

    if (action[0] === 'setElementAnswer') {
      console.debug('[EVAL] setElementAnswer', action[1]);
      const { blockId, value } = action[1];

      const block = this.definition.children
        ?.filter((c) => c.type === 'page')
        ?.flatMap((p: any) => p.children)
        ?.find((item) => item.id === blockId);

      if (block?.type === 'question') {
        const questionBlock = block as FlowBlockQuestion;
        const questionObject = this.bridge.objects.find(
          (object) =>
            object.objectType === 'question' &&
            object.id === questionBlock.questionId,
        ) as QuestionObject;
        const questionType = getQuestionType(questionObject);

        if (['Choice', 'CXJ'].includes(questionType)) {
          if (
            questionObject.maxChoiceCount === 1 &&
            ['', undefined].includes(
              this.form.getValues()[`${blockId}.choiceIds[0]`],
            )
          ) {
            this.form.setValue(`${blockId}.choiceIds[0]`, value);
          } else if (
            !(this.form.getValues()[`${blockId}.choiceIds`]?.length > 0)
          ) {
            this.form.setValue(`${blockId}.choiceIds`, value);
          }
        } else if (questionType === 'Text') {
          if (
            ['', undefined].includes(
              this.form.getValues()[`${blockId}.comment`],
            )
          ) {
            this.form.setValue(`${blockId}.comment`, value);
          }
        } else if (
          ['', null, undefined].includes(
            this.form.getValues()[`${blockId}.score`],
          )
        ) {
          if (Number.isInteger(value)) {
            this.form.setValue(`${blockId}.score`, value.toString());
          } else {
            this.form.setValue(
              `${blockId}.score`,
              value === 'positive' ? '1' : '0',
            );
          }
        }
      } else if (block.type === 'input') {
        if (['', null, undefined].includes(this.form.getValues()[blockId])) {
          const inputBlock = block as FlowBlockInput;
          if (['Date', 'Time'].includes(inputBlock.inputType)) {
            const dateValue =
              typeof value === 'string' ? parseISO(value) : value;
            this.form.setValue(blockId, dateValue);
          } else {
            this.form.setValue(blockId, value);
          }
        }
      } else if (block.type === 'legal') {
        if (this.form.getValues()[blockId] === undefined) {
          this.form.setValue(blockId, value);
        }
      }
    }
    if (action[0] === 'setTarget') {
      console.debug('[EVAL] setTarget', action[1]);
      this.stateManager.setTargetId(action[1].value);
    }
  }

  public applyActions(actions: Action[]): void {
    actions.forEach((action) => {
      this.applyAction(action);
    });
  }

  async evaluateStatements(statements: ConditionalStatement[]): Promise<void> {
    for (let i = 0; i < statements.length; i += 1) {
      const statement = statements[i];
      // we need to iterate over statements in order of definition

      await this.evaluateStatement(statement);
    }
  }
}
